import { useEffect, useRef } from 'react'

/**
 * Колесо фортуны — Canvas, рисунок один в один с утверждённым прототипом.
 *
 * Почему Canvas, а не SVG: секторов до 30, у каждого свой градиент и текст,
 * а вращение — одна CSS-трансформация контейнера. Так дешевле и на слабых
 * телефонах, и в коде.
 *
 * Вращение наружу не рисуется: родитель ставит transform на обёртку, поэтому
 * canvas перерисовывается только при смене набора призов. Ступица («SPX»),
 * указатель и обод — отдельные DOM-элементы поверх canvas, чтобы не вращаться.
 */

// Внутренний размер canvas. CSS растягивает его до размера окна, а все
// пропорции ниже считаются от этого числа — как в прототипе от его ширины.
const SIZE = 640

const TICKS = 60

// Радиальные градиенты секторов — ровно как в прототипе: глубокий изумруд
// для денег, графит для дней подписки, золото только у джекпота.
const SECTOR_STYLE = {
  money: {
    stops: ['#14503E', '#0E382B', '#071B14'],
    stroke: 'rgba(52, 211, 153, 0.20)',
    text: '#EAF7F1',
  },
  demo: {
    stops: ['#202020', '#171717', '#0B0B0B'],
    stroke: 'rgba(255, 255, 255, 0.09)',
    text: '#A6A6A6',
  },
  real: {
    stops: ['#2E2E2E', '#222222', '#101010'],
    stroke: 'rgba(255, 255, 255, 0.20)',
    text: '#D8D8D8',
  },
  jack: {
    stops: ['#6A4E18', '#45320F', '#1D1506'],
    stroke: 'rgba(255, 214, 102, 0.42)',
    text: '#FFE7A8',
  },
}

function usdLabel(usd) {
  const n = Number(usd) || 0
  return `$${n % 1 ? n.toFixed(2) : n.toFixed(0)}`
}

// Две строки подписи: «+1 дн.» и «демо» / «реал». У денег одна строка.
function sectorLines(p, labels) {
  if (p.kind === 'money') return [usdLabel(p.usd)]
  const day = labels?.day || 'дн.'
  const kind = p.kind === 'real'
    ? (labels?.real || 'реал')
    : (labels?.demo || 'демо')
  return [`+${p.days} ${day}`, kind]
}

function sectorStyle(p) {
  if (p.jack) return SECTOR_STYLE.jack
  return SECTOR_STYLE[p.kind] || SECTOR_STYLE.demo
}

export default function WheelCanvas({ prizes = [], rotation = 0, spinning = false, onSpinEnd, labels }) {
  const canvasRef = useRef(null)
  const transitionRef = useRef(false)

  // Новый прокрут — снова ждём transitionend (иначе после первого раза
  // срабатывала только страховая задержка).
  useEffect(() => {
    if (spinning) transitionRef.current = false
  }, [spinning])

  useEffect(() => {
    const cv = canvasRef.current
    if (!cv) return
    const dpr = Math.min(2, Math.max(1, window.devicePixelRatio || 1))
    cv.width = SIZE * dpr
    cv.height = SIZE * dpr
    const ctx = cv.getContext('2d')
    if (!ctx) return
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0)
    ctx.clearRect(0, 0, SIZE, SIZE)

    const c = SIZE / 2
    const r = c - SIZE * 0.012
    const n = Math.max(1, prizes.length)
    const step = (Math.PI * 2) / n
    const fs = SIZE * (n > 6 ? 0.041 : 0.046)

    for (let i = 0; i < n; i++) {
      const p = prizes[i] || {}
      const style = sectorStyle(p)
      // Сектор 0 стоит по центру сверху: a0 = −90° − полшага.
      const a0 = i * step - Math.PI / 2 - step / 2
      const a1 = a0 + step

      const g = ctx.createRadialGradient(c, c, r * 0.08, c, c, r)
      g.addColorStop(0, style.stops[0])
      g.addColorStop(0.55, style.stops[1])
      g.addColorStop(1, style.stops[2])

      ctx.beginPath()
      ctx.moveTo(c, c)
      ctx.arc(c, c, r, a0, a1)
      ctx.closePath()
      ctx.fillStyle = g
      ctx.fill()

      ctx.strokeStyle = style.stroke
      ctx.lineWidth = Math.max(1, SIZE * 0.0022)
      ctx.stroke()

      // Подписи вдоль радиуса, как в прототипе: читаются от центра наружу.
      ctx.save()
      ctx.translate(c, c)
      ctx.rotate(i * step - Math.PI / 2)
      ctx.textAlign = 'center'
      ctx.textBaseline = 'middle'
      ctx.font = `600 ${fs.toFixed(1)}px -apple-system, "Segoe UI", Roboto, sans-serif`
      ctx.fillStyle = style.text
      const lines = sectorLines(p, labels)
      const x = r * 0.60
      if (lines.length === 1) {
        ctx.fillText(lines[0], x, 0)
      } else {
        ctx.fillText(lines[0], x, -fs * 0.58)
        ctx.fillText(lines[1], x, fs * 0.58)
      }
      ctx.restore()
    }

    // Засечки по ободу — 60 рисок, читается как приборная шкала.
    ctx.save()
    ctx.translate(c, c)
    for (let t = 0; t < TICKS; t++) {
      const long = t % 5 === 0
      ctx.rotate((Math.PI * 2) / TICKS)
      ctx.beginPath()
      ctx.moveTo(0, -r)
      ctx.lineTo(0, -r + SIZE * (long ? 0.026 : 0.013))
      ctx.strokeStyle = long ? 'rgba(255,255,255,.16)' : 'rgba(255,255,255,.07)'
      ctx.lineWidth = Math.max(0.8, SIZE * 0.0016)
      ctx.stroke()
    }
    ctx.restore()

    // Внешнее кольцо.
    ctx.beginPath()
    ctx.arc(c, c, r, 0, Math.PI * 2)
    ctx.strokeStyle = 'rgba(255,255,255,.13)'
    ctx.lineWidth = Math.max(1.4, SIZE * 0.008)
    ctx.stroke()
  }, [prizes, labels])

  return (
    <div className="fortune-wheel-wrap">
      <div className="fortune-wheel-rim" aria-hidden="true" />
      <div
        className={`fortune-wheel-rotor${spinning ? ' is-moving' : ''}`}
        style={{
          transform: `rotate(${rotation}deg)`,
          transition: spinning ? 'transform 4.4s cubic-bezier(0.11, 0.74, 0.05, 1)' : 'none',
        }}
        onTransitionEnd={(e) => {
          // Только собственная transform-анимация ротора. transitionend
          // всплывает от детей и срабатывает на любые свойства — без этой
          // проверки «конец анимации» можно поймать в самом начале прокрута,
          // и уведомление ушло бы до остановки колеса.
          if (e.target !== e.currentTarget) return
          if (e.propertyName && e.propertyName !== 'transform') return
          if (!spinning || transitionRef.current) return
          transitionRef.current = true
          if (onSpinEnd) onSpinEnd()
        }}
      >
        <canvas ref={canvasRef} className="fortune-wheel-canvas" aria-hidden="true" />
      </div>
      <div className="fortune-wheel-hub" aria-hidden="true">SPX</div>
      <svg className="fortune-wheel-pointer" viewBox="0 0 18 16" aria-hidden="true">
        <path d="M9 15.2 1.2 1.6h15.6z" fill="#E9E9E9" />
      </svg>
    </div>
  )
}

export { sectorLines, sectorStyle, usdLabel }
